import { Box, Stack } from '@mui/material';
import useAgentsChats from './useAgentsChats';
import { ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import AgentInput from './agent-input/AgentInput';
import AgentTypeTag from './AgentTypeTag';
import AgentPrompts from './AgentPrompts';
import { colors, Typography } from '@sweep-io/sweep-design';
import { AI_AGENTS_INFO, CHAT_AREA_MAX_WIDTH } from './aiAgentsConsts';
import AgentIssues from './AgentIssues';
import { PromptGroupsMinimized } from './promptsLibrary/PromptGroupsMinimized';
import { isMetadataTypeAgent } from './helper';
import { useUnhappyStates } from '../pages/environments/useUnhappyStates';
import { AgentsEntryPoint } from '../pages/get-started/agentic-get-started/AgentsEntryPoint';
import { AgentInputMode } from './agent-input/agentInputModeTypes';
import BetaTag from '../common/BetaTag';
import { AgentType } from '@server/ai';
import useSendBiEvent from '../../hooks/useSendBiEvent';
import { AI_AGENTS_EVENTS } from '../../services/events';
import useAgentsHome from './useAgentsHome';
import { useSelector } from 'react-redux';
import { selectIsIssuesPanelOpen } from './agentsHomeReducer';

interface AgentChatEmptyStateProps {
  title?: string;
  removeSubtitle?: boolean;
  removeAgentTypeTag?: boolean;
  displayPromptsLibrary?: boolean;
  bottomCta?: React.ReactNode;
  maxWidth?: string;
  inputPlaceholder?: string;
  customSubtitle?: React.ReactNode;
  hideIssuesTable?: boolean;
  panelButtons?: ReactNode | null;
  displayAgentsEntryPoint?: boolean;
  setIsParentLoading?: (isLoading: boolean) => void;
}

const AgentChatEmptyState = ({
  title,
  removeSubtitle,
  removeAgentTypeTag = false,
  displayPromptsLibrary,
  bottomCta,
  maxWidth = CHAT_AREA_MAX_WIDTH,
  inputPlaceholder,
  customSubtitle,
  hideIssuesTable = false,
  panelButtons,
  displayAgentsEntryPoint = false,
  setIsParentLoading,
}: AgentChatEmptyStateProps) => {
  const { onConfirmMessage, activeAgent } = useAgentsChats();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { selectedReplyText, setReplyText } = useAgentsHome();
  const { isInitialFetch } = useUnhappyStates();
  const sendBiEvent = useSendBiEvent();

  const isIssuesPanelOpen = useSelector(selectIsIssuesPanelOpen);

  const underInputRender = useRef<HTMLDivElement>(null);

  const onConfirm = useCallback(
    async (
      message: string,
      options?: { advancedMode?: AgentInputMode; additionalOrgIds?: string[] },
    ) => {
      if (!activeAgent) {
        return;
      }
      setIsSubmitting(true);
      await onConfirmMessage({
        messageStr: message,
        agentId: activeAgent.id,
        agentType: activeAgent.type,
        replyTo: selectedReplyText || undefined,
        isFirstMessage: true,
        dontSendToServer: isInitialFetch,
        advancedMode: options?.advancedMode,
        additionalOrgIds: options?.additionalOrgIds,
      });
      setIsSubmitting(false);
      setReplyText(null);
    },
    [activeAgent, setReplyText, isInitialFetch, onConfirmMessage, selectedReplyText],
  );

  const onClickPrompt = useCallback(
    async (prompt: string) => {
      if (isSubmitting) {
        return;
      }
      await onConfirm(prompt);
    },
    [isSubmitting, onConfirm],
  );

  const onClickPromptFromLibrary = useCallback(
    async (prompt: string) => {
      sendBiEvent({ name: AI_AGENTS_EVENTS.promptsLibraryPromptClick });
      await onClickPrompt(prompt);
    },
    [onClickPrompt, sendBiEvent],
  );

  const { color, name, description, prompts, isSupportIssues } = activeAgent
    ? AI_AGENTS_INFO[activeAgent.type]
    : {
        color: '',
        name: '',
        description: '',
        prompts: { texts: [] },
        isSupportIssues: false,
      };

  const transformedPrompts = useMemo(
    () =>
      prompts?.texts
        ? prompts?.texts?.map((prompt) => ({
            id: prompt,
            name: prompt,
            promptText: prompt,
          }))
        : [],
    [prompts?.texts],
  );

  if (!activeAgent) {
    return null;
  }
  const displayTitle = title || activeAgent.name;

  const headerContent = (
    <Stack gap={2} alignItems="center" justifyContent="center" textAlign="center">
      <Box
        sx={{
          '.MuiTypography-root': {
            fontSize: '36px', // Exception from the design system, approved by UX as a one-off
            lineHeight: 'normal',
            fontWeight: '300',
          },
        }}
      >
        <Typography variant="h1">{displayTitle}</Typography>
      </Box>
      {!removeAgentTypeTag && (
        <Stack direction="row" alignItems="center" gap={1}>
          <AgentTypeTag size="small" color={color} text={name} />
          {activeAgent.type === AgentType.Permissions && <BetaTag />}
        </Stack>
      )}
      {!removeSubtitle && (
        <>
          {customSubtitle || (
            <Typography variant="body" color={colors.grey[700]}>
              {description}
            </Typography>
          )}
        </>
      )}
    </Stack>
  );

  const agentIssues = isSupportIssues && !hideIssuesTable && <AgentIssues agent={activeAgent} />;

  const agentInput = (
    <AgentInput
      onConfirm={onConfirm}
      isLoading={isSubmitting}
      disabled={false}
      selectedText={selectedReplyText}
      onClearSelectedText={() => setReplyText(null)}
      placeholderText={inputPlaceholder}
    />
  );

  const getPrompts = () => {
    if (displayPromptsLibrary) {
      return (
        <Stack flexWrap="wrap" gap={1}>
          <PromptGroupsMinimized onPromptClick={onClickPromptFromLibrary} />
        </Stack>
      );
    }
    if (prompts?.texts) {
      // Hide prompts for metadata type agents
      if (isMetadataTypeAgent(activeAgent)) {
        return null;
      }

      return (
        <Box alignSelf="flex-start" width="100%">
          <AgentPrompts
            prompts={transformedPrompts}
            onClickPrompt={onClickPrompt}
            isDisabled={isSubmitting}
          />
        </Box>
      );
    }
  };

  return (
    <Stack
      sx={{
        width: '100%',
        alignItems: 'center',
        overflow: 'hidden',
        height: '100%',
      }}
    >
      {/* When issues panel is open, panelButtons has its own absolute positioning from AgentsHomeContent */}
      {!!panelButtons && isIssuesPanelOpen && panelButtons}
      {/* When no side panel is open, we need to position the buttons */}
      {!!panelButtons && !isIssuesPanelOpen && (
        <Box
          sx={{
            position: 'absolute',
            right: '12px',
            top: '12px', //should be vertically aligned with the "agent name" in AgentChat, and in the same place as the buttons in collapsed mode (for smooth transition)
          }}
        >
          {panelButtons}
        </Box>
      )}
      <Stack
        alignItems="center"
        justifyContent="center"
        overflow="hidden"
        pt={2}
        width="100%"
        flex={1}
      >
        <Stack alignItems="center" height="100%" width="100%" overflow="auto">
          <Stack
            width="100%"
            p="24px 12px"
            gap={5}
            maxWidth={maxWidth}
            alignItems="center"
            justifyContent="center"
            flex={1}
          >
            {headerContent}
            {agentIssues}
            <Box width="100%">{agentInput}</Box>
            <Stack
              ref={underInputRender}
              alignItems="center"
              justifyContent="center"
              gap={'40px'}
              width="100%"
            >
              {getPrompts()}
              {displayAgentsEntryPoint && setIsParentLoading && (
                <AgentsEntryPoint
                  setIsLoading={setIsParentLoading}
                  popoverAnchorEl={underInputRender.current}
                />
              )}
            </Stack>

            {bottomCta && (
              <Box width="100%" maxWidth={maxWidth} p="12px" display="flex" justifyContent="center">
                {bottomCta}
              </Box>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default AgentChatEmptyState;
